<template>
  <div v-if="itemData.description" class="container-wrap py-9 tablet:py-12">
    <component
      :is="hasLink ? TheNuxtLink : 'div'"
      :id="itemData.trackingCode"
      v-bind="hasLink ? { to: provideFormatUrl(itemData.link) } : {}"
      :class="[backgroundClass, hasLink && hoverClass]"
      :title="hasLink && itemData.description"
      class="block rounded-2xl px-4 py-4 transition-shadow tablet:flex tablet:items-center tablet:px-8 tablet:py-6"
    >
      <div
        class="mb-2 bg-clip-text text-2xl font-bold text-transparent tablet:mb-0 tablet:w-60 tablet:basis-60 tablet:text-3xl"
        :class="titleClass"
      >
        Announcement
      </div>
      <div class="tablet:flex-1 tablet:pl-4">
        <p class="text-sm leading-6 text-gray-700 desktop:text-base">
          {{ itemData.description }}
        </p>
      </div>
      <div v-if="hasLink" class="hidden w-7 basis-7 pl-1 tablet:block">
        <IconArrow direction="right" class="h-6 w-6" />
      </div>
    </component>
  </div>
</template>

<script setup lang="ts">
  interface IAnnouncement {
    description: string;
    link?: string;
    trackingCode?: string;
  }

  interface IProps {
    itemData: IAnnouncement;
  }

  const props = withDefaults(defineProps<IProps>(), {
    itemData: () => ({
      description: '',
      link: undefined,
      trackingCode: undefined,
    }),
  });

  const solutionsStore = useSolutionsStore();
  const { themeStyle } = storeToRefs(solutionsStore);
  const { provideFormatUrl } = useFormatUrl();
  const TheNuxtLink = resolveComponent('TheNuxtLink');

  const hasLink = computed((): boolean => !!props.itemData.link);

  const titleClass = computed((): string => {
    switch (themeStyle.value) {
      case 'sustainable':
        return 'bg-gradient-to-r from-brand-400 to-green-400';
      case 'connected':
        return 'bg-gradient-to-r from-brand-400 to-cyan-400';
      case 'ai':
        return 'bg-gradient-to-r from-brand-400 to-purple-400';
      case 'operations':
        return 'bg-gradient-to-r from-brand-400 to-yellow-400';
      default:
        return 'bg-gradient-to-r from-brand-400 to-blue-400';
    }
  });

  const backgroundClass = computed((): string => {
    switch (themeStyle.value) {
      case 'sustainable':
        return 'bg-gradient-to-bl from-green-150 to-green-50';
      case 'connected':
        return 'bg-gradient-to-bl from-cyan-150 to-cyan-50';
      case 'ai':
        return 'bg-gradient-to-bl from-purple-150 to-purple-50';
      case 'operations':
        return 'bg-gradient-to-bl from-yellow-150 to-yellow-50';
      default:
        return 'bg-gradient-to-bl from-blue-150 to-blue-50';
    }
  });

  const hoverClass = computed((): string => {
    switch (themeStyle.value) {
      case 'sustainable':
        return 'hover:shadow-button hover:shadow-green-400 active:shadow-button active:shadow-green-400';
      case 'connected':
        return 'hover:shadow-button hover:shadow-cyan-400 active:shadow-button active:shadow-cyan-400';
      case 'ai':
        return 'hover:shadow-button hover:shadow-purple-400 active:shadow-button active:shadow-purple-400';
      case 'operations':
        return 'hover:shadow-button hover:shadow-yellow-400 active:shadow-button active:shadow-yellow-400';
      default:
        return 'hover:shadow-button hover:shadow-blue-400 active:shadow-button active:shadow-brand-400';
    }
  });

  export type { IAnnouncement };
</script>
